// API client module
import { getAuthToken } from '../state/store.js';

const API_BASE = import.meta.env.VITE_API_URL || '';

// Helper for auth headers without Content-Type (for multipart uploads)
function getAuthHeadersMultipart() {
  const token = getAuthToken();
  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

// Helper function to get auth headers
function getAuthHeaders() {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
}

// Login function
export async function login(username, password) {
  const response = await fetch(`${API_BASE}/api/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      username,
      password,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Login failed');
  }

  return response.json();
}

// Get available collections
export async function getCollections() {
  const response = await fetch(`${API_BASE}/api/documentation/collections`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to load collections');
  }

  return response.json();
}

// Get branding (app name, tagline, welcome text, privacy link).
// Public endpoint; sends the token when present so group overrides apply.
// Anonymous embeds may pass a public group (see PUBLIC_GROUPS).
export async function getBranding(group = null) {
  const url = group
    ? `${API_BASE}/api/config/branding?group=${encodeURIComponent(group)}`
    : `${API_BASE}/api/config/branding`;
  const response = await fetch(url, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to load branding');
  }

  return response.json();
}

// Admin: branding editor data (scopes with stored + effective values)
export async function getAdminBranding() {
  const response = await fetch(`${API_BASE}/api/admin/branding`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to load branding settings');
  }

  return response.json();
}

// Admin: save branding overrides for a scope (empty fields = fall back)
export async function saveAdminBranding(scope, values) {
  const response = await fetch(
    `${API_BASE}/api/admin/branding/${encodeURIComponent(scope)}`,
    {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(values),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to save branding settings');
  }

  return response.json();
}

// Create a new knowledge base (ChromaDB collection)
export async function createCollection(name, displayName = '') {
  const response = await fetch(`${API_BASE}/api/admin/collections`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ name, display_name: displayName }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to create knowledge base');
  }

  return response.json();
}

// Update a knowledge base's display name
export async function updateCollection(name, displayName) {
  const response = await fetch(
    `${API_BASE}/api/admin/collections/${encodeURIComponent(name)}`,
    {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ display_name: displayName }),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to update knowledge base');
  }

  return response.json();
}

// Delete a knowledge base; force deletes even when non-empty
export async function deleteCollection(name, force = false) {
  const response = await fetch(
    `${API_BASE}/api/admin/collections/${encodeURIComponent(name)}?force=${force ? 'true' : 'false'}`,
    {
      method: 'DELETE',
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to delete knowledge base');
  }

  return response.json();
}

// Get documents in a collection
export async function getDocumentsInCollection(collection = 'user_faq') {
  const response = await fetch(
    `${API_BASE}/api/documentation/documents?collection=${encodeURIComponent(collection)}`,
    {
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error('Failed to load documents');
  }

  return response.json();
}

// Get documents by audience (uses backend audience->collection resolver)
export async function getDocumentsForAudience(
  audience = 'end_user',
  includeMetadata = false,
  options = {}
) {
  return fetchDocuments({ audience }, includeMetadata, options);
}

// Get documents from an explicit collection (knowledge base)
export async function getDocumentsForCollection(
  collection,
  includeMetadata = false,
  options = {}
) {
  return fetchDocuments({ collection }, includeMetadata, options);
}

async function fetchDocuments(target, includeMetadata, options = {}) {
  const params = new URLSearchParams();
  if (target.collection) {
    params.set('collection', target.collection);
  } else {
    params.set('audience', target.audience);
  }

  if (includeMetadata) {
    params.set('include_metadata', 'true');
  }

  const staleOnly = Boolean(options.staleOnly);
  const staleDays = Number.parseInt(String(options.staleAfterDays ?? ''), 10);
  if (staleOnly && Number.isFinite(staleDays) && staleDays > 0) {
    params.set('stale_after_days', String(staleDays));
  }
  if (staleOnly && options.includeUnknownStale) {
    params.set('include_unknown_stale', 'true');
  }

  const allowedSortModes = new Set(['name', 'age_desc', 'age_asc']);
  const sortBy = options.sortBy && allowedSortModes.has(options.sortBy)
    ? options.sortBy
    : (staleOnly ? 'age_desc' : 'name');
  params.set('sort_by', sortBy);

  const response = await fetch(`${API_BASE}/api/documentation/documents?${params.toString()}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to load documents');
  }

  return response.json();
}

// Get documentation chunks (with pagination)
export async function getDocumentationChunks(collection = 'user_faq', documents = [], limit = 200, offset = 0) {
  let url = `${API_BASE}/api/documentation/chunks?collection=${encodeURIComponent(collection)}&limit=${limit}&offset=${offset}`;

  if (documents && documents.length > 0) {
    url += `&documents=${encodeURIComponent(documents.join(','))}`;
  }

  const response = await fetch(url, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to load documentation');
  }

  return response.json();
}

// Get documentation chunks by audience (uses backend audience->collection resolver)
export async function getDocumentationChunksForAudience(audience = 'end_user', documents = [], limit = 50) {
  let url = `${API_BASE}/api/documentation/chunks?audience=${encodeURIComponent(audience)}&limit=${limit}`;

  if (documents && documents.length > 0) {
    url += `&documents=${encodeURIComponent(documents.join(','))}`;
  }

  const response = await fetch(url, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to load documentation');
  }

  return response.json();
}

// Semantic search in documentation
export async function searchDocumentation(query, collection = 'user_faq', limit = 20, documents = []) {
  let url = `${API_BASE}/api/documentation/search?query=${encodeURIComponent(query)}&collection=${encodeURIComponent(collection)}&limit=${limit}`;
  
  if (documents && documents.length > 0) {
    url += `&documents=${encodeURIComponent(documents.join(','))}`;
  }
  
  const response = await fetch(url, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Semantic search failed');
  }

  return response.json();
}

// Send chat message with streaming response
export async function sendChatMessage(userInput, messageHistory, onChunk, documentFilter = [], sessionId = null, audience = null, collection = null, group = null) {
  const body = {
    user_input: userInput,
    message_history: messageHistory,
    document_filter: documentFilter,
    session_id: sessionId,
  };
  if (audience) {
    body.audience = audience;
  }
  if (collection) {
    body.collection = collection;
  }
  if (group) {
    body.group = group;
  }

  const response = await fetch(`${API_BASE}/api/chat`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    // Non-JSON bodies (rate limiter, proxy error pages) must not mask the
    // real failure with a JSON parse error — fall back to the HTTP status.
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Chat request failed (HTTP ${response.status})`);
  }
  if (!response.body) {
    throw new Error('Chat stream is unavailable');
  }

  // Read the stream
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let fullAnswer = '';
  let sseBuffer = '';

  const handleSseEvent = (eventBlock) => {
    if (!eventBlock || !eventBlock.trim()) return null;

    const dataLines = eventBlock
      .split(/\r?\n/)
      .filter((line) => line.startsWith('data:'))
      .map((line) => line.slice(5).trimStart());

    if (dataLines.length === 0) return null;

    let data;
    try {
      data = JSON.parse(dataLines.join('\n'));
    } catch {
      // Ignore malformed/incomplete events safely.
      return null;
    }

    if (data.error) {
      throw new Error(data.error);
    }

    if (data.done) {
      return { done: true, messageId: data.message_id || null };
    }

    if (typeof data.chunk === 'string') {
      fullAnswer += data.chunk;
      if (onChunk) {
        onChunk(data.chunk);
      }
    }

    return null;
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      sseBuffer += decoder.decode(value, { stream: true });
      const eventBlocks = sseBuffer.split(/\r?\n\r?\n/);
      sseBuffer = eventBlocks.pop() || '';

      for (const block of eventBlocks) {
        const result = handleSseEvent(block);
        if (result?.done) {
          return { answer: fullAnswer, messageId: result.messageId };
        }
      }
    }

    // Flush any trailing decoder buffer and parse last event block if complete.
    sseBuffer += decoder.decode();
    const finalResult = handleSseEvent(sseBuffer);
    if (finalResult?.done) {
      return { answer: fullAnswer, messageId: finalResult.messageId };
    }
  } finally {
    reader.releaseLock();
  }

  return { answer: fullAnswer, messageId: null };
}

// Upload a document (admin)
export async function uploadDocument(file, documentName, collection) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('document_name', documentName);
  formData.append('collection', collection);

  const response = await fetch(`${API_BASE}/api/admin/documents/upload`, {
    method: 'POST',
    headers: getAuthHeadersMultipart(),
    body: formData,
  });

  if (!response.ok) {
    if (response.status === 413) {
      throw new Error('Datei zu groß (Maximum: 50 MB).');
    }
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Upload failed');
  }

  return response.json();
}

// Delete a document (admin)
export async function deleteDocument(documentName, collection) {
  const response = await fetch(
    `${API_BASE}/api/admin/documents/${encodeURIComponent(documentName)}?collection=${encodeURIComponent(collection)}`,
    {
      method: 'DELETE',
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Delete failed');
  }

  return response.json();
}

// Replace a document (admin)
export async function replaceDocument(documentName, file, collection) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('collection', collection);

  const response = await fetch(
    `${API_BASE}/api/admin/documents/${encodeURIComponent(documentName)}/replace`,
    {
      method: 'PUT',
      headers: getAuthHeadersMultipart(),
      body: formData,
    }
  );

  if (!response.ok) {
    if (response.status === 413) {
      throw new Error('Datei zu groß (Maximum: 50 MB).');
    }
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Replace failed');
  }

  return response.json();
}

// Send feedback for a message
export async function sendFeedback(messageId, sessionId, rating, comment = null) {
  const response = await fetch(`${API_BASE}/api/feedback`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      message_id: messageId,
      session_id: sessionId,
      rating,
      comment,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Feedback submission failed');
  }

  return response.json();
}

// Notify backend that session ended
export async function endSession(sessionId) {
  try {
    await fetch(`${API_BASE}/api/analytics/session-end`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ session_id: sessionId }),
      keepalive: true,
    });
  } catch (e) {
    // Best effort on page unload
  }
}

// Fetch analytics summary (admin)
export async function getAnalyticsSummary(fromDate = null, toDate = null, audience = null) {
  const params = new URLSearchParams();
  if (fromDate) params.append('from_date', fromDate);
  if (toDate) params.append('to_date', toDate);
  if (audience) params.append('audience', audience);

  const response = await fetch(`${API_BASE}/api/admin/analytics/summary?${params.toString()}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to load analytics');
  }

  return response.json();
}

// Fetch daily trends (admin)
export async function getAnalyticsTrends(fromDate = null, toDate = null, audience = null) {
  const params = new URLSearchParams();
  if (fromDate) params.append('from_date', fromDate);
  if (toDate) params.append('to_date', toDate);
  if (audience) params.append('audience', audience);

  const response = await fetch(`${API_BASE}/api/admin/analytics/trends?${params.toString()}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to load analytics trends');
  }

  return response.json();
}

// Fetch top questions (admin)
export async function getAnalyticsTopQuestions(fromDate = null, toDate = null, audience = null, limit = 10) {
  const params = new URLSearchParams();
  if (fromDate) params.append('from_date', fromDate);
  if (toDate) params.append('to_date', toDate);
  if (audience) params.append('audience', audience);
  params.append('limit', limit.toString());

  const response = await fetch(`${API_BASE}/api/admin/analytics/top-questions?${params.toString()}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to load top questions');
  }

  return response.json();
}

// Fetch peak hours (admin)
export async function getAnalyticsPeakHours(fromDate = null, toDate = null, audience = null) {
  const params = new URLSearchParams();
  if (fromDate) params.append('from_date', fromDate);
  if (toDate) params.append('to_date', toDate);
  if (audience) params.append('audience', audience);

  const response = await fetch(`${API_BASE}/api/admin/analytics/peak-hours?${params.toString()}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to load peak hours');
  }

  return response.json();
}

// ---------------------------------------------------------------------------
// Call Center analytics (admin)
// ---------------------------------------------------------------------------

export async function getCallcenterSummary(fromDate = null, toDate = null, agentAlias = null) {
  const params = new URLSearchParams();
  if (fromDate) params.append('from_date', fromDate);
  if (toDate) params.append('to_date', toDate);
  if (agentAlias) params.append('agent_alias', agentAlias);

  const response = await fetch(`${API_BASE}/api/callcenter/summary?${params}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) throw new Error('Failed to load callcenter summary');
  return response.json();
}

export async function getCallcenterReviews(fromDate = null, toDate = null, agentAlias = null, rating = null, limit = 50) {
  const params = new URLSearchParams();
  if (fromDate) params.append('from_date', fromDate);
  if (toDate) params.append('to_date', toDate);
  if (agentAlias) params.append('agent_alias', agentAlias);
  if (rating) params.append('rating', rating);
  params.append('limit', String(limit));

  const response = await fetch(`${API_BASE}/api/callcenter/reviews?${params}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) throw new Error('Failed to load callcenter reviews');
  return response.json();
}

export async function getCallcenterAgents(fromDate = null, toDate = null) {
  const params = new URLSearchParams();
  if (fromDate) params.append('from_date', fromDate);
  if (toDate) params.append('to_date', toDate);

  const response = await fetch(`${API_BASE}/api/callcenter/agents?${params}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) throw new Error('Failed to load callcenter agents');
  return response.json();
}

export function getCallcenterExportUrl(fromDate = null, toDate = null, agentAlias = null, rating = null) {
  const params = new URLSearchParams();
  if (fromDate) params.append('from_date', fromDate);
  if (toDate) params.append('to_date', toDate);
  if (agentAlias) params.append('agent_alias', agentAlias);
  if (rating) params.append('rating', rating);
  return `${API_BASE}/api/callcenter/export?${params}`;
}

// Fetch negatively rated questions (admin)
export async function getAnalyticsQuestions(fromDate = null, toDate = null, audience = null, limit = 50) {
  const params = new URLSearchParams();
  if (fromDate) params.append('from_date', fromDate);
  if (toDate) params.append('to_date', toDate);
  if (audience) params.append('audience', audience);
  params.append('limit', limit.toString());

  const response = await fetch(`${API_BASE}/api/admin/analytics/questions?${params.toString()}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to load analytics questions');
  }

  return response.json();
}
